import { useMutation } from '@apollo/client/react';
import { notify } from '@components/ui/molecules/Toast';
import { LoginDocument } from '@graphql/types';
import { useCallback } from 'react';

import type { LoginInput, LoginResponse } from '../types';

export const useLogin = () => {
  const [loginMutation, { loading: loginLoading }] = useMutation(LoginDocument);
  /**
   * Executes the login mutation and returns the payload or throws.
   */
  const doLogin = useCallback(
    async (data: LoginInput): Promise<LoginResponse> => {
      const response = await loginMutation({
        variables: { input: data },
      });
      const loginRes = response.data?.login;

      if (!loginRes) {
        notify.error({ title: 'Login failed', description: 'No response from server' });
        throw new Error('Login failed');
      }

      // Check if login was successful (user, accessToken, and refreshToken are present)
      const isSuccessful = loginRes.user && loginRes.accessToken && loginRes.refreshToken;
      const hasErrors = loginRes.errors && loginRes.errors.length > 0;

      if (hasErrors || !isSuccessful) {
        const errorMessage =
          loginRes.errors?.[0]?.message || 'Invalid email or password. Please try again.';

        notify.error({ title: 'Login failed', description: errorMessage });
      } else {
        notify.success({ title: 'Login successful' });
      }

      return loginRes as LoginResponse;
    },
    [loginMutation]
  );

  return { doLogin, loginLoading };
};
